import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, Lock, ArrowRight, Users, AlertCircle, LogIn } from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { collection, query, where, getDocs, updateDoc, doc, arrayUnion, getDoc } from 'firebase/firestore';
import { useIdentity } from '../context/IdentityContext';

const randomNames = [
    'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
    'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
    'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer',
    'Surge', 'Flare', 'Cosmo', 'Daze', 'Sable', 'Nyx', 'Rift', 'Glow', 'Specter',
    'Chill', 'Havoc', 'Mist', 'Stryke', 'Zest', 'Dawn', 'Volt', 'Crimson', 'Slate',
    'Kaze', 'Fury', 'Vapor', 'Glint', 'Rebel', 'Ash', 'Tide', 'Zenith', 'Dagger',
    'Star', 'Nerve', 'Blitz', 'Shadez', 'Flux', 'Neon', 'Chaos', 'Verve', 'Rush',
    'Grit', 'Faze', 'Drip', 'Hex', 'Jolt', 'Wisp', 'Scorch', 'Vex', 'Hype', 'Frosty',
    'Slash', 'Glaze', 'Sparkz', 'Eclipse', 'Raze', 'Sway', 'Phantomz', 'Buzz', 'Quartz',
    'Vivid', 'RUSH', 'Craze', 'Driftz', 'Ignite', 'Zest', 'Frenzy', 'Spectral', 'BlazeX',
    'Vaporz', 'Sting', 'Aurora', 'Kryo'
];
const getRandomAlias = () => randomNames[Math.floor(Math.random() * randomNames.length)];

export const JoinRoomPage = () => {
    const { identityMode } = useIdentity();
    const navigate = useNavigate();
    const { code: urlCode } = useParams();
    
    const [codeInput, setCodeInput] = useState(urlCode || '');
    const [passwordInput, setPasswordInput] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [error, setError] = useState('');
    const [foundRoom, setFoundRoom] = useState(null); // { id, data }
    const [needsPassword, setNeedsPassword] = useState(false);

    const [currentUser, setCurrentUser] = useState(auth.currentUser);
    const [isAuthLoading, setIsAuthLoading] = useState(!auth.currentUser);

    // Auth gate: wait for Firebase auth state to resolve
    useEffect(() => {
        const unsub = auth.onAuthStateChanged((u) => {
            setCurrentUser(u);
            setIsAuthLoading(false);
            if (!u) {
                const redirectPath = urlCode ? `/room/${urlCode}` : '/join-room';
                navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`, { replace: true });
            }
        });
        return () => unsub();
    }, [navigate, urlCode]);

    // Auto-lookup room if opened via shared deep link /room/:code
    useEffect(() => {
        if (urlCode && currentUser && !isJoining && !foundRoom && !error) {
            lookupRoom(urlCode, currentUser);
        }
    }, [urlCode, currentUser]);

    const lookupRoom = async (code, activeUser = currentUser) => {
        const targetUser = activeUser || auth.currentUser;
        if (!targetUser) return;

        setError('');
        setIsJoining(true);
        
        try {
            const q = query(collection(db, 'customVoiceRooms'), where('code', '==', code));
            const snap = await getDocs(q);
            
            if (snap.empty) {
                setError('Room not found. Check the code and try again.');
                setIsJoining(false);
                return;
            }

            const roomDoc = snap.docs[0];
            const roomData = roomDoc.data();

            // Check if room is still open
            if (!roomData.isOpen) {
                setError('This room has been closed by the host.');
                setIsJoining(false);
                return;
            }

            // Check if already in the room
            if (roomData.participantUids?.includes(targetUser.uid)) {
                navigate(`/custom-voice-room/${roomDoc.id}`, { replace: true, state: { roomCode: code } });
                return;
            }

            // Check if room is full
            if ((roomData.participantUids?.length || 0) >= (roomData.maxSize || 8)) {
                setError(`Room is full (${roomData.maxSize || 8}/${roomData.maxSize || 8}).`);
                setIsJoining(false);
                return;
            }

            // Check if password is required
            if (roomData.password) {
                setFoundRoom({ id: roomDoc.id, data: roomData });
                setNeedsPassword(true);
                setIsJoining(false);
                return;
            }

            // No password — join directly
            await joinRoom(roomDoc.id, roomData, targetUser);
        } catch (e) {
            setError('Something went wrong. Please try again.');
            setIsJoining(false);
        }
    };

    const joinRoom = async (roomId, roomData, activeUser = currentUser) => {
        const targetUser = activeUser || auth.currentUser;
        if (!targetUser) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', targetUser.uid));
            const uData = userDoc.data() || {};
            const myAlias = getRandomAlias();

            await updateDoc(doc(db, 'customVoiceRooms', roomId), {
                participantUids: arrayUnion(targetUser.uid),
                [`participantInfo.${targetUser.uid}`]: {
                    alias: myAlias,
                    college: uData.college || 'Unknown',
                    voiceEffect: null,
                    muted: false
                }
            });
            
            logEvent(analytics, 'custom_room_joined', {
                join_method: urlCode ? 'link' : 'code'
            });

            navigate(`/custom-voice-room/${roomId}`, { replace: true, state: { roomCode: roomData?.code } });
        } catch (e) {
            setError('Failed to join room. Please try again.');
            setIsJoining(false);
        }
    };

    const handleCodeSubmit = () => {
        const code = codeInput.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
        if (code.length < 4) {
            setError('Please enter a valid room code.');
            return;
        }
        lookupRoom(code);
    };

    const handlePasswordSubmit = async () => {
        if (!passwordInput.trim()) {
            setError('Please enter the room password.');
            return;
        }
        
        if (passwordInput.trim() !== foundRoom.data.password) {
            setError('Incorrect password. Try again.');
            return;
        }

        setIsJoining(true);
        setError('');
        await joinRoom(foundRoom.id, foundRoom.data);
    };

    if (identityMode === 'real') {
       return (
         <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
            <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
               <LogIn size={32} />
            </div>
            <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">Join Room</h2>
            <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
         </main>
       );
    }

    // ========== PASSWORD PROMPT ==========
    if (needsPassword && foundRoom) {
        return (
            <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white pb-24 font-poppins selection:bg-[#0BD9E5]/30">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,102,0,0.12),transparent_70%)]" />
                </div>

                <div className="relative z-10 flex flex-col h-full p-6">
                    <div className="flex items-center justify-between mb-12 mt-4">
                        <button onClick={() => { setNeedsPassword(false); setFoundRoom(null); setError(''); }} className="text-gray-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full">
                            <X size={24} />
                        </button>
                        <h1 className="text-xl font-bold flex items-center gap-2">
                            <Lock size={18} className="text-[#FF6600]" /> Password Required
                        </h1>
                        <div className="w-10"></div>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center -mt-16">
                        <div className="w-20 h-20 rounded-full bg-[#FF6600]/10 border border-[#FF6600]/30 flex items-center justify-center mb-8">
                            <Lock size={32} className="text-[#FF6600]" />
                        </div>
                        
                        <h2 className="text-2xl font-black mb-2">{foundRoom.data.name}</h2>
                        <p className="text-sm text-gray-500 mb-8">{foundRoom.data.participantUids?.length || 0}/{foundRoom.data.maxSize || 8} people in room</p>

                        <div className="w-full max-w-sm">
                            <input
                                type="password"
                                value={passwordInput}
                                onChange={(e) => { setPasswordInput(e.target.value); setError(''); }}
                                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                                placeholder="Enter room password"
                                className="w-full bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 text-white placeholder-gray-600 outline-none focus:border-[#FF6600]/50 transition-colors text-lg text-center tracking-widest mb-4"
                                autoFocus
                            />
                            
                            {error && (
                                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/30 mb-4">
                                    <AlertCircle size={14} className="text-red-500" />
                                    <p className="text-xs text-red-400">{error}</p>
                                </div>
                            )}

                            <button 
                                onClick={handlePasswordSubmit}
                                disabled={isJoining}
                                className="w-full h-14 bg-[#FF6600] text-white font-bold uppercase tracking-widest rounded-xl hover:bg-[#FF6600]/90 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                            >
                                {isJoining ? 'Joining...' : 'Join Room'}
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    // ========== MAIN JOIN SCREEN ==========
    return (
        <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white pb-36 sm:pb-6 font-poppins selection:bg-[#0BD9E5]/30">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,102,0,0.1),transparent_70%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
            </div>

            <div className="relative z-10 flex flex-col min-h-screen p-6 pb-36 sm:pb-6">
                <div className="flex items-center justify-between mb-12 mt-4">
                    <button onClick={() => navigate('/custom-rooms', { replace: true })} className="text-gray-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full">
                        <X size={24} />
                    </button>
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Users size={18} className="text-[#FF6600]" /> Join Room
                    </h1>
                    <div className="w-10"></div>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center -mt-16">
                    <h2 className="text-4xl font-black mb-4 tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                        Enter Code
                    </h2>
                    <p className="text-sm text-gray-500 mb-10">Enter the room code shared with you</p>

                    <div className="w-full max-w-sm">
                        <input
                            type="text"
                            value={codeInput}
                            onChange={(e) => { 
                                setCodeInput(e.target.value.toUpperCase()); 
                                setError(''); 
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && handleCodeSubmit()}
                            placeholder="XXXX-XXXX"
                            maxLength={9}
                            className="w-full bg-gray-900 border border-gray-800 rounded-2xl px-5 py-5 text-white placeholder-gray-600 outline-none focus:border-[#FF6600]/50 transition-colors text-2xl text-center tracking-[0.3em] font-black mb-4"
                            autoFocus
                        />

                        {error && (
                            <motion.div 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 mb-4"
                            >
                                <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                                <p className="text-xs text-red-400">{error}</p>
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Join Button */}
                <div className="mt-auto pt-8 flex justify-center w-full">
                    <button 
                        onClick={handleCodeSubmit}
                        disabled={isJoining || codeInput.trim().length < 4}
                        className="w-full max-w-sm h-16 bg-white text-black font-bold uppercase tracking-widest rounded-xl hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isJoining ? 'Finding Room...' : <><ArrowRight size={20} /> Join Room</>}
                    </button>
                </div>
            </div>
        </main>
    );
};

export default JoinRoomPage;

